import React, { useEffect, useState, useRef, useCallback } from 'react';
import { placeLabel } from '../lib/placeLabel';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import {
  MapPin, Clock, Plane, Users, Briefcase, Phone, MessageCircle, Navigation2,
  CheckCircle2, Loader2, AlertCircle, CalendarPlus, Timer, Plus, Trash2,
  X, ChevronRight, Copy, Mail, Receipt, Camera,
} from 'lucide-react';
import { computeEndTime } from '../lib/time';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAPS_LIBRARIES = ['places'];
const OPERATOR_TEL = '03333357733';
const OPERATOR_DISPLAY = '0333 335 7733';
const OFFICE_EMAIL = 'info@obsidianchauffeurs.co.uk';
const RECEIPT_CATEGORIES = ['Parking', 'Tolls', 'Fuel'];

const navUrls = (destination, origin = '') => {
  const enc = (s) => encodeURIComponent(s || '');
  return {
    google: origin
      ? `https://www.google.com/maps/dir/?api=1&destination=${enc(destination)}&origin=${enc(origin)}&travelmode=driving`
      : `https://www.google.com/maps/dir/?api=1&destination=${enc(destination)}&travelmode=driving`,
    apple: origin
      ? `https://maps.apple.com/?saddr=${enc(origin)}&daddr=${enc(destination)}&dirflg=d`
      : `https://maps.apple.com/?daddr=${enc(destination)}&dirflg=d`,
    waze: `https://waze.com/ul?q=${enc(destination)}&navigate=yes`,
  };
};

const NavSheet = ({ open, onClose, destination, urls }) => {
  if (!open) return null;
  const items = [
    { key: 'google', label: 'Google Maps', sub: 'Default for most Android phones', url: urls.google, accent: '#1A73E8' },
    { key: 'apple', label: 'Apple Maps', sub: 'iOS native navigation', url: urls.apple, accent: '#000000' },
    { key: 'waze', label: 'Waze', sub: 'Live traffic, community alerts', url: urls.waze, accent: '#33CCFF' },
  ];
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="pj-nav-sheet"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 pb-8 shadow-2xl">
        <div className="flex items-start justify-between mb-5 gap-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Navigate to</p>
            <p className="text-lg font-semibold text-black break-words" data-testid="pj-nav-destination">{destination}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full shrink-0" aria-label="Close" data-testid="pj-nav-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-2">
          {items.map((it) => (
            <a
              key={it.key}
              href={it.url}
              target="_blank"
              rel="noreferrer"
              onClick={onClose}
              className="flex items-center gap-4 p-4 rounded-2xl border border-neutral-200 hover:border-black hover:bg-neutral-50 transition-all"
              data-testid={`pj-nav-${it.key}`}
            >
              <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: it.accent }}>
                <Navigation2 className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-black">{it.label}</p>
                <p className="text-xs text-neutral-500 truncate">{it.sub}</p>
              </div>
              <ChevronRight className="h-5 w-5 text-neutral-400" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

const STATUS_FLOW = [
  { key: 'on_the_way', label: 'On The Way' },
  { key: 'arrived', label: 'Arrived' },
  { key: 'pob', label: 'Passenger On Board' },
  { key: 'completed', label: 'Completed' },
];

const PublicJob = () => {
  const { token } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState('');
  const [extras, setExtras] = useState({ waitingMinutes: '', extraMiles: '', notes: '', stops: [] });
  const [savingExtras, setSavingExtras] = useState(false);
  const [rcpt, setRcpt] = useState({ category: 'Parking', amount: '', note: '', file: null });
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [showNav, setShowNav] = useState(false);
  const [navDestination, setNavDestination] = useState('');
  const stopAcRefs = useRef({});
  const { isLoaded: gmapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GMAPS_LIBRARIES,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/job/${token}`);
      setJob(data.booking);
      const te = data.booking?.tripExtras;
      if (te) {
        setExtras({
          waitingMinutes: te.waitingMinutes ? String(te.waitingMinutes) : '',
          extraMiles: te.extraMiles ? String(te.extraMiles) : '',
          notes: te.notes || '',
          stops: Array.isArray(te.additionalStops) ? te.additionalStops : [],
        });
      }
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'This job link is invalid or has expired');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    if (!window.confirm(`Update status to "${STATUS_FLOW.find((s) => s.key === status)?.label}"?`)) return;
    setUpdating(status);
    try {
      await axios.post(`${API}/public/job/${token}/status`, { status });
      toast.success('Status updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update status');
    } finally {
      setUpdating('');
    }
  };

  const addStop = () => setExtras((p) => ({ ...p, stops: [...(p.stops || []), { location: '', note: '' }] }));
  const updateStop = (i, v) => setExtras((p) => ({ ...p, stops: p.stops.map((s, idx) => (idx === i ? { ...s, location: v } : s)) }));
  const removeStop = (i) => setExtras((p) => ({ ...p, stops: p.stops.filter((_, idx) => idx !== i) }));

  const saveExtras = async () => {
    setSavingExtras(true);
    try {
      await axios.post(`${API}/public/job/${token}/extras`, {
        waitingMinutes: parseFloat(extras.waitingMinutes) || 0,
        extraMiles: parseFloat(extras.extraMiles) || 0,
        notes: extras.notes || '',
        additionalStops: (extras.stops || []).filter((s) => (s.location || '').trim()),
      });
      toast.success('Trip extras saved · dispatch notified');
      await load();
    } catch (e) {
      toast.error('Could not save trip extras');
    } finally {
      setSavingExtras(false);
    }
  };

  const submitReceipt = async () => {
    if (!rcpt.amount || parseFloat(rcpt.amount) <= 0) { toast.error('Please enter the amount'); return; }
    setSavingReceipt(true);
    try {
      const fd = new FormData();
      fd.append('category', rcpt.category);
      fd.append('amount', rcpt.amount);
      fd.append('note', rcpt.note || '');
      if (rcpt.file) fd.append('file', rcpt.file);
      await axios.post(`${API}/public/job/${token}/receipt`, fd);
      toast.success('Receipt submitted');
      setRcpt({ category: 'Parking', amount: '', note: '', file: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit receipt');
    } finally {
      setSavingReceipt(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-white animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center p-6" data-testid="public-job-error">
        <div className="text-center max-w-sm">
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-4" />
          <p className="text-white text-lg font-semibold">{error}</p>
          <p className="text-neutral-400 text-sm mt-2">Please contact Obsidian Chauffeurs dispatch for assistance.</p>
        </div>
      </div>
    );
  }

  const isCompleted = job.driverStatus === 'completed';
  const isAsDirected = !!job.asDirectedHours
    || job.serviceType === 'As Directed Service'
    || (job.dropoffLocation || '').toLowerCase().startsWith('as directed');
  const end = job.asDirectedHours ? computeEndTime(job.pickupTime, job.asDirectedHours) : null;

  // Ordered navigable stops: pickup -> stopovers -> dropoff (skip the fake As-Directed drop-off)
  const destinations = [];
  if (job.pickupLocation) destinations.push({ key: 'pickup', label: 'Pickup', address: job.pickupLocation });
  (Array.isArray(job.stopovers) ? job.stopovers : []).forEach((s, i) => {
    if (s) destinations.push({ key: `stop-${i}`, label: `Stop ${i + 1}`, address: s });
  });
  if (!isAsDirected && job.dropoffLocation) destinations.push({ key: 'dropoff', label: 'Drop-off', address: job.dropoffLocation });

  const openNavigateTo = (address) => {
    if (!address) return;
    setNavDestination(address);
    setShowNav(true);
  };

  const copyAddress = async (text) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(text);
      toast.success('Address copied');
    } catch (e) {
      toast.error('Could not copy the address');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="public-job-page">
      {/* Header */}
      <div className="px-5 pb-6 border-b border-white/10" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 2rem)' }}>
        <p className="text-[10px] tracking-[0.35em] uppercase text-white/40">Obsidian Chauffeurs</p>
        <h1 className="text-2xl font-bold mt-1">Your Assigned Job</h1>
        <p className="text-sm text-white/50 mt-1 font-mono">{job.id}</p>
        <div className="mt-3 inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs">
          <span className={`h-2 w-2 rounded-full ${isCompleted ? 'bg-emerald-400' : 'bg-amber-400'}`} />
          {job.driverStatus ? job.driverStatus.replace(/_/g, ' ').toUpperCase() : 'ASSIGNED'}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-5 py-6 space-y-4">
        {/* Journey */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          {isAsDirected && (
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-[11px] uppercase tracking-wider text-white/70" data-testid="pj-as-directed-badge">
              As Directed · {job.asDirectedHours || ''} hrs
            </div>
          )}
          <div className="space-y-3">
            {destinations.map((dest, i) => {
              const isPickup = dest.key === 'pickup';
              const isDropoff = dest.key === 'dropoff';
              const dotCls = isPickup
                ? 'h-3 w-3 rounded-full bg-emerald-400'
                : isDropoff
                  ? 'h-3 w-3 rounded-sm bg-red-500'
                  : 'h-2.5 w-2.5 rounded-full bg-amber-400';
              return (
                <div key={dest.key} className="flex items-start gap-3" data-testid={`pj-journey-row-${dest.key}`}>
                  <div className="flex flex-col items-center pt-1.5 shrink-0">
                    <div className={dotCls} />
                    {i < destinations.length - 1 && <div className="w-px h-7 bg-white/15 mt-1" />}
                  </div>
                  <button
                    type="button"
                    onClick={() => copyAddress(dest.address)}
                    className="flex-1 min-w-0 text-left -mx-1 px-1 py-0.5 rounded-md hover:bg-white/5 group"
                    data-testid={`pj-copy-address-${dest.key}`}
                    title="Tap to copy address"
                  >
                    <p className="text-[10px] uppercase tracking-wider text-white/40">{dest.label}</p>
                    <p className="text-sm font-semibold text-white break-words leading-snug inline-flex items-start gap-1.5">
                      <span>{dest.address}</span>
                      <Copy className="h-3 w-3 text-white/30 group-hover:text-white/60 shrink-0 mt-1" />
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => openNavigateTo(dest.address)}
                    className="shrink-0 h-9 w-9 rounded-full bg-white/10 hover:bg-white hover:text-black text-white flex items-center justify-center transition-colors"
                    aria-label={`Navigate to ${dest.label}`}
                    data-testid={`pj-row-nav-${dest.key}`}
                  >
                    <Navigation2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-white/10">
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-white/40" />
              <span className="text-sm">{job.pickupDate} · {job.pickupTime}{end ? ` – ${end}` : ''}</span>
            </div>
            {job.flightNumber && (
              <div className="flex items-center gap-2"><Plane className="h-4 w-4 text-white/40" /><span className="text-sm">{job.flightNumber}</span></div>
            )}
            {job.passengers && (
              <div className="flex items-center gap-2"><Users className="h-4 w-4 text-white/40" /><span className="text-sm">{job.passengers} pax</span></div>
            )}
            {job.luggage && (
              <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-white/40" /><span className="text-sm">{job.luggage} bags</span></div>
            )}
          </div>
          {job.vehicleName && <p className="text-xs text-white/50">Vehicle: {job.vehicleName}</p>}
          {job.specialRequests && <p className="text-xs text-white/60 bg-white/5 rounded-lg p-3">Note: {job.specialRequests}</p>}
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => openNavigateTo((destinations[0] || {}).address)}
            disabled={!destinations.length}
            className="bg-white text-black rounded-2xl py-4 flex items-center justify-center gap-2 font-semibold hover:bg-white/90 disabled:opacity-40"
            data-testid="pj-navigate"
          >
            <Navigation2 className="h-5 w-5" /> Navigate
          </button>
          {job.customerPhone ? (
            <a href={`tel:${job.customerPhone}`} className="bg-white/10 border border-white/15 rounded-2xl py-4 flex items-center justify-center gap-2 font-semibold hover:bg-white/15" data-testid="pj-call">
              <Phone className="h-5 w-5" /> Call {job.customerName?.split(' ')[0]}
            </a>
          ) : (
            <div className="bg-white/5 rounded-2xl py-4 flex items-center justify-center gap-2 text-white/30 text-sm">
              <Phone className="h-5 w-5" /> Contact hidden
            </div>
          )}
        </div>
        {job.customerPhone && (
          <a href={`https://wa.me/${job.customerPhone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="w-full bg-white/10 border border-white/15 rounded-2xl py-3 flex items-center justify-center gap-2 font-semibold hover:bg-white/15" data-testid="pj-whatsapp">
            <MessageCircle className="h-5 w-5" /> WhatsApp Customer
          </a>
        )}
        <a href={`${API}/public/job/${token}/calendar.ics`} className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 flex items-center justify-center gap-2 font-semibold hover:bg-white/10 text-sm" data-testid="pj-calendar">
          <CalendarPlus className="h-5 w-5" /> Add to my Calendar
        </a>

        {/* Contact operator */}
        <div className="grid grid-cols-2 gap-3">
          <a href={`tel:${OPERATOR_TEL}`} className="bg-white/10 border border-white/15 rounded-2xl py-3.5 flex items-center justify-center gap-2 font-semibold hover:bg-white/15" data-testid="pj-call-operator">
            <Phone className="h-5 w-5" /> Call Operator
          </a>
          <a href={`mailto:${OFFICE_EMAIL}`} className="bg-white/10 border border-white/15 rounded-2xl py-3.5 flex items-center justify-center gap-2 font-semibold hover:bg-white/15" data-testid="pj-email-office">
            <Mail className="h-5 w-5" /> Email Office
          </a>
        </div>
        <p className="text-center text-[11px] text-white/40 -mt-1">Operator · {OPERATOR_DISPLAY}</p>

        {/* Status flow */}
        {!isCompleted && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-3">Update Status</p>
            <div className="grid grid-cols-2 gap-2.5">
              {STATUS_FLOW.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setStatus(s.key)}
                  disabled={!!updating || job.driverStatus === s.key}
                  className={`rounded-xl py-3.5 text-sm font-semibold transition-colors disabled:opacity-40 ${job.driverStatus === s.key ? 'bg-emerald-500 text-white' : 'bg-white/10 hover:bg-white/20'}`}
                  data-testid={`pj-status-${s.key}`}
                >
                  {updating === s.key ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Trip extras */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="pj-trip-extras">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-1 flex items-center gap-2"><Timer className="h-3 w-3" /> Trip Extras</p>
          <p className="text-xs text-white/40 mb-4">Log any waiting time, extra stops or mileage so dispatch can bill correctly.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/40">Waiting (min)</label>
              <input type="number" min="0" value={extras.waitingMinutes} onChange={(e) => setExtras((p) => ({ ...p, waitingMinutes: e.target.value }))} className="w-full mt-1 h-11 bg-white/10 border border-white/15 rounded-xl px-3 text-base focus:outline-none focus:border-white text-white" data-testid="pj-extra-waiting" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/40">Extra miles</label>
              <input type="number" min="0" value={extras.extraMiles} onChange={(e) => setExtras((p) => ({ ...p, extraMiles: e.target.value }))} className="w-full mt-1 h-11 bg-white/10 border border-white/15 rounded-xl px-3 text-base focus:outline-none focus:border-white text-white" data-testid="pj-extra-miles" />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <label className="text-[10px] uppercase tracking-wider text-white/40">Additional stops</label>
            {(extras.stops || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                {gmapsLoaded ? (
                  <Autocomplete onLoad={(ac) => { stopAcRefs.current[i] = ac; }} onPlaceChanged={() => { const p = stopAcRefs.current[i]?.getPlace(); const a = placeLabel(p); if (a) updateStop(i, a); }} className="flex-1">
                    <input placeholder="Search stop address" value={s.location || ''} onChange={(e) => updateStop(i, e.target.value)} className="w-full h-11 bg-white/10 border border-white/15 rounded-xl px-3 text-sm focus:outline-none focus:border-white text-white" data-testid={`pj-stop-${i}`} />
                  </Autocomplete>
                ) : (
                  <input placeholder="Stop location" value={s.location || ''} onChange={(e) => updateStop(i, e.target.value)} className="flex-1 h-11 bg-white/10 border border-white/15 rounded-xl px-3 text-sm focus:outline-none focus:border-white text-white" data-testid={`pj-stop-${i}`} />
                )}
                <button type="button" onClick={() => removeStop(i)} className="h-11 w-11 shrink-0 rounded-xl bg-white/10 text-white/60 hover:text-red-400 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button type="button" onClick={addStop} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/80 hover:text-white" data-testid="pj-add-stop"><Plus className="h-3.5 w-3.5" /> Add stop</button>
          </div>
          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-wider text-white/40">Notes</label>
            <textarea rows={2} value={extras.notes} onChange={(e) => setExtras((p) => ({ ...p, notes: e.target.value }))} placeholder="Anything dispatch should know…" className="w-full mt-1 bg-white/10 border border-white/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-white resize-none text-white" data-testid="pj-extra-notes" />
          </div>
          <button type="button" onClick={saveExtras} disabled={savingExtras} className="mt-4 w-full bg-white text-black rounded-xl py-3 font-semibold hover:bg-white/90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="pj-save-extras">
            {savingExtras ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save trip extras
          </button>
        </div>

        {/* Submit receipt */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="pj-receipt">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-1 flex items-center gap-2"><Receipt className="h-3 w-3" /> Submit a Receipt</p>
          <p className="text-xs text-white/40 mb-4">Log parking, tolls or fuel paid on this job.</p>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {RECEIPT_CATEGORIES.map((c) => (
              <button key={c} type="button" onClick={() => setRcpt((p) => ({ ...p, category: c }))}
                className={`h-11 rounded-xl text-sm font-semibold border transition-colors ${rcpt.category === c ? 'bg-white text-black border-white' : 'bg-white/10 text-white/80 border-white/15 hover:bg-white/15'}`}
                data-testid={`pj-receipt-cat-${c.toLowerCase()}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/40">Amount (£)</label>
              <input type="number" min="0" step="0.01" value={rcpt.amount} onChange={(e) => setRcpt((p) => ({ ...p, amount: e.target.value }))} className="w-full mt-1 h-11 bg-white/10 border border-white/15 rounded-xl px-3 text-base focus:outline-none focus:border-white text-white" placeholder="0.00" data-testid="pj-receipt-amount" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/40">Note</label>
              <input type="text" value={rcpt.note} onChange={(e) => setRcpt((p) => ({ ...p, note: e.target.value }))} className="w-full mt-1 h-11 bg-white/10 border border-white/15 rounded-xl px-3 text-sm focus:outline-none focus:border-white text-white" placeholder="Optional" data-testid="pj-receipt-note" />
            </div>
          </div>
          <label className="mt-3 flex items-center gap-3 h-12 px-4 rounded-xl border border-dashed border-white/20 cursor-pointer hover:border-white/50" data-testid="pj-receipt-photo-label">
            <Camera className="h-5 w-5 text-white/50" />
            <span className="text-sm text-white/60 truncate">{rcpt.file ? rcpt.file.name : 'Add a photo (optional)'}</span>
            <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={(e) => setRcpt((p) => ({ ...p, file: e.target.files?.[0] || null }))} data-testid="pj-receipt-photo" />
          </label>
          <button type="button" onClick={submitReceipt} disabled={savingReceipt} className="mt-4 w-full bg-white text-black rounded-xl py-3 font-semibold hover:bg-white/90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="pj-receipt-submit">
            {savingReceipt ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Submit Receipt
          </button>
        </div>

        <p className="text-center text-[11px] text-white/30 pt-2 pb-8">Obsidian Chauffeurs · Confidential</p>
      </div>

      <NavSheet
        open={showNav}
        onClose={() => setShowNav(false)}
        destination={navDestination}
        urls={navUrls(navDestination, job.pickupLocation)}
      />
    </div>
  );
};

export default PublicJob;
